import { Flex, Text, Spinner, Tooltip, Box } from '@chakra-ui/react';
import {
  faArrowRightArrowLeft,
  faInfoCircle
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Switch, Table, useToast } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useUpdateTransferNetworkConfig } from 'src/api/mutations';
import { getTransferNetworkConfig } from 'src/api/queries';

const renderArticleTypes = (articleTypes: string[], journalId: string) => {
  const more = articleTypes.slice(3);
  return (
    <Flex gap={2} wrap="wrap">
      {articleTypes.slice(0, 3).map(articleType => (
        <Badge key={`${journalId}-${articleType}`} whiteSpace="normal" wordBreak="break-word">{articleType}</Badge>
      ))}
      {!!more.length && (
        <Tooltip hasArrow borderRadius={4} label={more.join(', ')}>
          <Box>
            <Badge>+{more.length}</Badge>
          </Box>
        </Tooltip>
      )}
    </Flex>
  );
};

const AvailableJournals = () => {
  const { journalId = '' } = useParams();
  const toast = useToast();

  const updateTransferNetworkConfig = useUpdateTransferNetworkConfig(journalId);

  const { data, isLoading } = useQuery({
    queryKey: ['transfer-network-config', { journalId }],
    queryFn: () => getTransferNetworkConfig(journalId),
    retry: 0
  });

  const onChange = async (
    checked: boolean,
    journalId: string,
    journalName: string
  ) => {
    const response = await updateTransferNetworkConfig.mutateAsync({
      add: checked ? [journalId] : [],
      remove: !checked ? [journalId] : []
    });

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: (
          <Text>
            Transfers to {journalName} {checked ? 'Activated' : 'Deactivated'}
          </Text>
        ),
        isClosable: true
      });
    } else {
      toast({
        variant: 'error',
        title: response.message,
        isClosable: true
      });
    }
  };

  return (
    <Flex flexDirection={'column'} gap={4}>
      <Text textStyle="headingGroup">Available Transfers</Text>
      <Text textStyle="textBody">
        Transfers can be configured to Journals that accept Submissions
        according to the current Journal’s Article Types.
      </Text>
      {isLoading ? (
        <Spinner />
      ) : !data?.length ? (
        <Flex flexDirection={'column'} gap={2} alignItems="center">
          <Flex
            width={'100%'}
            padding={8}
            justifyContent="center"
            background="neutralSecondaryFill"
            borderRadius="lg"
            fontSize="48px"
          >
            <FontAwesomeIcon icon={faArrowRightArrowLeft} />
          </Flex>
          <Flex
            flexDirection={'column'}
            gap={2}
            alignItems="center"
            marginY={4}
          >
            <Text textStyle="headingGroup">No Journals found.</Text>
          </Flex>
        </Flex>
      ) : (
        <Box overflowX="auto" width="100%">
        <Box minWidth="560px">
        <Table>
          <Table.HeaderRow gap={2}>
            <Table.HeaderCell flex="5">Journal Name</Table.HeaderCell>
            <Table.HeaderCell flex="5">
              <Flex gap={2}>
                <Text>Supported Article Types</Text>
                <Tooltip
                  hasArrow
                  placement="top"
                  borderRadius={4}
                  label="Shows which article types this journal can receive."
                >
                  <Text color="actionSecondaryContent">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </Text>
                </Tooltip>
              </Flex>
            </Table.HeaderCell>
            <Table.HeaderCell flex="2" minWidth="96px">
              Enable Transfers
            </Table.HeaderCell>
          </Table.HeaderRow>
          <Table.Body>
            {data.map(item => (
              <Table.Row key={item.id} gap={2}>
                <Table.RowCell flex="5" paddingY={2}>
                  <Text isTruncated>{item.journalName}</Text>
                </Table.RowCell>
                <Table.RowCell flex="5" paddingY={2}>
                  <Text>{renderArticleTypes(item.articleTypes, item.id)}</Text>
                </Table.RowCell>
                <Table.RowCell flex="2" minWidth="96px" paddingY={2}>
                  <Switch
                    isChecked={item.enabled}
                    onChange={({ target: { checked } }) =>
                      onChange(checked, item.id, item.journalName)
                    }
                    isDisabled={updateTransferNetworkConfig.isLoading}
                  />
                </Table.RowCell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        </Box>
        </Box>
      )}
    </Flex>
  );
};

export default AvailableJournals;
